import type { Chain } from '@pkg/evm'
import {
  createAbstractChain,
  createApeChain,
  createArbitrumNovaChain,
  createArbitrumOneChain,
  createAstarChain,
  createAvalancheCChain,
  createBaseChain,
  createBeamChain,
  createBerachain,
  createBitTorrentChain,
  createBlastChain,
  createBscChain,
  createCeloChain,
  createChilizChain,
  // createDefiVerseChain,
  createEthereumChain,
  createEtherlinkChain,
  createFraxtalChain,
  createGateLayerChain,
  createGeekVerseChain,
  createGnosisChain,
  createHyperEvmChain,
  createImmutableZkEvmChain,
  createKaiaChain,
  createKatanaChain,
  createLineaChain,
  createMantaPacificChain,
  createMantleChain,
  createMegaEthChain,
  createMemeCoreChain,
  createMetisChain,
  createModeChain,
  createOasysChain,
  createOpBnbChain,
  createOptimismChain,
  createPlasmaChain,
  createPolygonPosChain,
  createRobinhoodChain,
  createRoninChain,
  // createSaakuruChain,
  createScrollChain,
  createSeiChain,
  createSoneiumChain,
  createSonicChain,
  createTaikoChain,
  createTelosChain,
  createUnichain,
  createWorldChain,
  createXdcNetworkChain,
  createZkSyncEraChain,
} from '@pkg/evm'

type CreateEvmChainListParameters = {
  proxyApiEndpoint?: string
  etherscanApiKey: string
  kaiascanApiKey: string
  oasysProxyUrl?: string
}

/**
 * 新規登録・同期・解析に利用する対応中の EVM chain を生成する。
 * @param params explorer API キーと proxy の接続設定。
 * @returns 対応終了 chain を含まない chain 一覧。
 */
export const createEvmChainList = ({
  proxyApiEndpoint,
  etherscanApiKey,
  kaiascanApiKey,
  oasysProxyUrl,
}: CreateEvmChainListParameters): readonly Chain[] => [
  createAbstractChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createApeChain({ explorerProxyUrl: proxyApiEndpoint }),
  createArbitrumNovaChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createArbitrumOneChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createAstarChain({}),
  createAvalancheCChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createBaseChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createBeamChain(
    proxyApiEndpoint === undefined
      ? {}
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createBerachain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createBitTorrentChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createBscChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createBlastChain(
    proxyApiEndpoint === undefined
      ? {}
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createCeloChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createChilizChain(
    proxyApiEndpoint === undefined
      ? {}
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  // createDefiVerseChain({}),
  createEthereumChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createEtherlinkChain({ explorerProxyUrl: proxyApiEndpoint }),
  createFraxtalChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createGateLayerChain({ explorerProxyUrl: proxyApiEndpoint }),
  createGeekVerseChain({}),
  createGnosisChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createHyperEvmChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createImmutableZkEvmChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createKaiaChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: kaiascanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createKatanaChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createLineaChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createMantaPacificChain({ explorerProxyUrl: proxyApiEndpoint }),
  createMantleChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createMegaEthChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createMemeCoreChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createMetisChain({
    explorerProxyUrl: proxyApiEndpoint,
  }),
  createModeChain({ explorerProxyUrl: proxyApiEndpoint }),
  createOasysChain(
    proxyApiEndpoint === undefined
      ? { explorerProxyUrl: oasysProxyUrl }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createOpBnbChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createOptimismChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createPlasmaChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createPolygonPosChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  // createPolygonZkEvmChain(
  //   proxyApiEndpoint === undefined
  //     ? { explorerApiKey: etherscanApiKey }
  //     : { explorerProxyUrl: proxyApiEndpoint },
  // ),
  createRobinhoodChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createRoninChain(
    proxyApiEndpoint === undefined
      ? {}
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  // createSaakuruChain({}),
  createScrollChain(
    proxyApiEndpoint === undefined
      ? {}
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createSeiChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createSoneiumChain({}),
  createSonicChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createTaikoChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createTelosChain({ explorerProxyUrl: proxyApiEndpoint }),
  createUnichain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createWorldChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createXdcNetworkChain(
    proxyApiEndpoint === undefined
      ? { explorerApiKey: etherscanApiKey }
      : { explorerProxyUrl: proxyApiEndpoint },
  ),
  createZkSyncEraChain({}),
]
